import {ApiClient} from '@hconnect/apiclient'
import {useQuery} from '@tanstack/react-query'
import moment, {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import {getOptimizedSchedulesPath} from '../api'
import type {OptimizedSchedule} from '../api'

import type {MomentRange} from './useCurrentScheduleQuery'

const DS_API_BASE_PATH = '/hcem/ui'

export const OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY = 'optimizerRunsAndSchedules'

interface UseOptimizedScheduleQueryParams {
  plantId: string
  scheduleId?: number | undefined
  enabled?: boolean
  throwOnError?: boolean
  range: MomentRange
}

interface OptimizerRunsAndSchedulesResponse {
  optimizerRuns: unknown[]
  optimizedSchedules: OptimizedSchedule[]
}

const selectScheduleItemsFrom = (
  schedules: OptimizedSchedule[],
  date: Moment
): OptimizedSchedule[] => {
  return schedules.map((schedule) => ({
    ...schedule,
    schedules: Object.fromEntries(
      Object.entries(schedule.schedules).filter(([, scheduleItem]) =>
        moment.utc(scheduleItem.end).isAfter(date.clone().utc())
      )
    )
  }))
}

export const useOptimizedScheduleQuery = ({
  plantId,
  scheduleId,
  enabled = true,
  throwOnError = true,
  range: [startOfPlan]
}: UseOptimizedScheduleQueryParams) => {
  const selector = useCallback(
    ({optimizedSchedules}: OptimizerRunsAndSchedulesResponse) => {
      return selectScheduleItemsFrom(optimizedSchedules, startOfPlan).find(
        (schedule) => schedule.scheduleId === scheduleId
      )
    },
    [scheduleId, startOfPlan]
  )

  return useQuery<OptimizerRunsAndSchedulesResponse, unknown, OptimizedSchedule | undefined>({
    queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode: plantId}],
    queryFn: async ({signal}) => {
      const [optimizerRunsResponse, optimizedSchedulesResponse] = await Promise.all([
        ApiClient.axiosInstance.get(`${DS_API_BASE_PATH}/v2/optimizer/${plantId}/runs`, {signal}),
        ApiClient.axiosInstance.get<OptimizedSchedule[]>(getOptimizedSchedulesPath(plantId), {
          signal
        })
      ])
      return {
        optimizerRuns: optimizerRunsResponse.data,
        optimizedSchedules: optimizedSchedulesResponse.data
      }
    },
    enabled: enabled && scheduleId !== undefined,
    throwOnError,
    select: selector,
    ...(enabled && scheduleId !== undefined ? {} : {gcTime: 0})
  })
}
